import EmailOutlined from "@mui/icons-material/EmailOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Backdrop,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  useSendEmailVerificationOtpMutation,
  useVerifyEmailOtpMutation,
} from "api/api-auth";
import PhoneVerificationGate from "components/Login/PhoneVerificationGate";
import { SUPPORT_EMAIL } from "codes";
import logout from "helpers/logout";
import { type ReactNode, useState } from "react";
import { useAppDispatch } from "redux/hooks";
import { toast } from "react-toastify";
import type { CurrentUser } from "types";

const dialogPaperProps = {
  sx: {
    borderRadius: 3,
    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.2)",
  },
};
const dialogBackdropProps = {
  sx: { backgroundColor: "rgba(0, 0, 0, 0.32)" },
};

type EmailVerificationGateProps = {
  children: ReactNode;
  user?: CurrentUser;
  isUserError: boolean;
  isLoggedIn: boolean;
  refreshCurrentUser: () => Promise<CurrentUser>;
};

const EmailVerificationGate = ({
  children,
  user,
  isUserError,
  isLoggedIn,
  refreshCurrentUser,
}: EmailVerificationGateProps) => {
  const dispatch = useAppDispatch();
  const [sendVerificationCode, { isLoading: isSending }] =
    useSendEmailVerificationOtpMutation();
  const [verifyEmail, { isLoading: isVerifying }] = useVerifyEmailOtpMutation();
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [verificationFailures, setVerificationFailures] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isBusy = isSending || isVerifying || isRefreshing;

  const sendCode = async () => {
    try {
      await sendVerificationCode().unwrap();
      setCodeSent(true);
      setVerificationFailures(0);
      toast.success("A verification code has been sent to your email address.");
    } catch (error: any) {
      setVerificationFailures((failures) => failures + 1);
      toast.error(
        verificationFailures + 1 >= 2
          ? `We couldn't send a verification code. Please try again later or contact ${SUPPORT_EMAIL}.`
          : (error?.data?.message ??
              error?.message ??
              "Unable to send verification code."),
      );
    }
  };

  const confirmCode = async () => {
    try {
      setIsRefreshing(true);
      await verifyEmail({ code: code.trim() }).unwrap();
      const refreshedUser = await refreshCurrentUser();
      if (refreshedUser.email_verified !== true) {
        throw new Error(
          "Verification status has not updated. Please try again.",
        );
      }
      setCode("");
      setVerificationFailures(0);
      toast.success("Your email address has been verified.");
    } catch (error: any) {
      setVerificationFailures((failures) => failures + 1);
      toast.error(
        verificationFailures + 1 >= 2
          ? `We couldn't verify your email. Please try again later or contact ${SUPPORT_EMAIL}.`
          : (error?.data?.message ??
              error?.message ??
              "Unable to verify the code."),
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = async () => {
    await logout(dispatch);
  };

  // Keep query consumers mounted while account loading/error states change.
  // Replacing their wrapper restarts mount-time requests and can cause a retry loop.
  const protectedChildren = (
    <div
      ref={(element) => {
        if (element) element.inert = !user && !isUserError;
      }}
    >
      {children}
    </div>
  );

  if (!isLoggedIn) return null;

  if (user?.email_verified === true) {
    return (
      <PhoneVerificationGate
        user={user}
        onLogout={handleLogout}
        refreshCurrentUser={refreshCurrentUser}
      >
        {protectedChildren}
      </PhoneVerificationGate>
    );
  }

  if (isUserError) {
    return (
      <>
        {protectedChildren}
        <Dialog
          open
          onClose={() => undefined}
          disableEscapeKeyDown
          fullWidth
          maxWidth="sm"
          PaperProps={dialogPaperProps}
          BackdropProps={dialogBackdropProps}
        >
          <DialogTitle sx={{ px: { xs: 3, sm: 4 }, pt: 3, pb: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <EmailOutlined color="primary" />
              <Typography component="span" variant="h6" fontWeight={700}>
                Checking your account
              </Typography>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 3, sm: 4 }, py: 2 }}>
            <DialogContentText>
              We couldn&apos;t load your verification status. Please retry or
              log out.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: { xs: 3, sm: 4 }, pb: 3, gap: 1 }}>
            <Button
              onClick={() => {
                refreshCurrentUser().catch(() => undefined);
              }}
              variant="contained"
            >
              Retry
            </Button>
            <Button onClick={handleLogout} variant="outlined">
              Log out
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  if (!user) {
    return (
      <>
        {protectedChildren}
        <Box
          component="span"
          role="status"
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
          }}
        >
          Loading
        </Box>
        <Backdrop
          open
          invisible
          sx={{ zIndex: (theme) => theme.zIndex.modal - 1 }}
        />
      </>
    );
  }

  return (
    <>
      {protectedChildren}
      <Dialog
        open
        onClose={() => undefined}
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
        PaperProps={dialogPaperProps}
        BackdropProps={dialogBackdropProps}
      >
        <DialogTitle sx={{ px: { xs: 3, sm: 4 }, pt: 3, pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                display: "grid",
                placeItems: "center",
                width: 42,
                height: 42,
                borderRadius: "50%",
                bgcolor: "primary.50",
                color: "primary.main",
              }}
            >
              <EmailOutlined />
            </Box>
            <Box>
              <Typography component="div" variant="h6" fontWeight={700}>
                Confirm your email address
              </Typography>
              <Typography variant="body2" color="text.secondary">
                One quick step before you continue
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 3, sm: 4 }, py: 2 }}>
          <DialogContentText sx={{ mb: 2 }}>
            {codeSent
              ? `We sent a verification code to ${user.email}.`
              : `Send a verification code to ${user.email}.`}{" "}
            Confirm your email address to continue.
          </DialogContentText>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Can&apos;t access this email or receive a code? Contact{" "}
            <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link> or log
            out to sign in with another account.
          </Typography>
          {codeSent && (
            <TextField
              autoFocus
              fullWidth
              label="Verification code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputProps={{ inputMode: "numeric", maxLength: 8 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 3, sm: 4 }, pb: 3, gap: 1 }}>
          <Button onClick={handleLogout} variant="outlined" disabled={isBusy}>
            Log out
          </Button>
          {codeSent && (
            <Button onClick={sendCode} variant="text" disabled={isBusy}>
              Resend code
            </Button>
          )}
          <LoadingButton
            variant="contained"
            onClick={codeSent ? confirmCode : sendCode}
            loading={isBusy}
            disabled={isBusy || (codeSent && !code.trim())}
          >
            {codeSent ? "Verify email" : "Send verification code"}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailVerificationGate;
